import { recursiveReadonly } from "@p0security/shared/types/util";
import {
  AnyAccessType,
  IntegrationResourceRule,
  RequestorRule,
  ResourceRule,
} from "@p0security/shared/types/workflow/types";

export const YAML_RULE_SEARCH_PARAM = "yaml";
export const DEFAULT_TAB_SEARCH_PARAM = "defaultTab";

export const DEFAULT_NEW_REQUESTOR: RequestorRule = { type: "any" };
export const DEFAULT_NEW_RESOURCE: ResourceRule = { type: "any" };

// We're having to do this backwards as well, due to js-schema-generator not allowing for generics or derived types.
// So we have to use the types as the source of truth instead of these constants.
// And these constants ensure type exhaustiveness of all filter types for every resource.
export const filterTypeOptionsByResource: {
  readonly [K in IntegrationResourceRule["service"]]: {
    readonly [F in keyof NonNullable<
      Extract<IntegrationResourceRule, { service: K }>["filters"]
    >]: string;
  };
} = {
  aws: {
    tag: "Tag",
    group: "Group",
    "permission-set": "Permission Set",
    policy: "Policy",
    resource: "Resource",
    role: "Role",
    account: "Account",
  },
  "azure-ad": { group: "Group", role: "Role" },
  "entra-id": { group: "Group", role: "Role" },
  custom: { application: "Application", item: "Item" },
  gcloud: {
    project: "Project",
    permission: "Permission",
    role: "Role",
    resource: "Resource",
  },
  k8s: { role: "Role", resource: "Resource", cluster: "Cluster" },
  mcp: { role: "Role" },
  mysql: { instance: "Instance", role: "Role" },
  okta: { group: "Group" },
  oci: { groupId: "Group", domainId: "Domain", compartmentId: "Compartment" },
  pg: {
    role: "Role",
    instance: "Instance",
    "row-level": "Row-level policy",
    "row-level-value": "Row-level binding value",
  },
  "self-hosted": {
    environment: "Environment",
    host: "Host",
    sudo: "Sudo privilege",
    user: "User",
  },
  snowflake: { role: "Role" },
  "splunk-stack": { stackNode: "Stack Node", role: "Role" },
  ssh: {
    // Note:the labels are pluralized in the UI, e.g. Google Cloud instance -> Google Cloud instances
    provider: "Cloud Provider",
    destination: "Destination",
    group: "Group",
    parent: "Parent resource",
    sudo: "Sudo privilege",
    region: "Region",
  },
  tailscale: { organization: "Organization", device: "Device", tag: "Tag" },
  workspace: { group: "Group" },
  azure: { resource: "Resource", role: "Role", subscription: "Subscription" },
  github: { team: "Team" },
  rdp: { destination: "Destination", provider: "Provider", region: "Region" },
  "zscaler-private-access": {
    tenant: "Tenant",
    "application-segment": "Application Segment",
  },
  "cisco-secure-access": { organization: "Organization", resource: "Resource" },
  windows: { account: "Account", domain: "Domain" },
};

export const accessTypeOptionsByResource: {
  [Service in IntegrationResourceRule["service"]]: Omit<
    {
      [AccessType in NonNullable<
        Extract<IntegrationResourceRule, { service: Service }>["accessType"]
      >]: true;
    },
    AnyAccessType
  >;
} = {
  "azure-ad": { membership: true, assignment: true },
  "cisco-secure-access": { resource: true },
  "entra-id": { membership: true, assignment: true },
  "self-hosted": { computer: true, user: true },
  "splunk-stack": { stack: true },
  "zscaler-private-access": { "application-segment": true },
  aws: {
    group: true,
    "permission-set": true,
    policy: true,
    resource: true,
    role: true,
  },
  azure: { resource: true },
  custom: { resource: true },
  gcloud: { permission: true, role: true, resource: true },
  github: { team: true },
  k8s: { resource: true },
  mcp: { role: true },
  mysql: { role: true },
  oci: { group: true },
  okta: { membership: true },
  pg: { sql: true, role: true },
  // ENG-6341 Azure RDP: Implement RDP access type options
  rdp: { session: true },
  snowflake: { sql: true, role: true },
  ssh: { session: true, group: true, parent: true, all: true },
  tailscale: { device: true, tag: true, ssh: true },
  windows: { account: true },
  workspace: { membership: true },
};

const keyOptions = recursiveReadonly({
  arn: { label: "ARN", value: "arn" },
  id: { label: "ID", value: "id" },
  label: { label: "Label", value: "label" },
  name: { label: "Name", value: "name" },
  type: { label: "Type", value: "type" },
  "full-resource-name": {
    label: "Fully Qualified Resource Name",
    value: "full-resource-name",
  },
});

const sshKeyOptions = recursiveReadonly({
  arn: { label: "AWS ARN", value: "arn" },
  group: { label: "Group", value: "group" },
  instanceName: { label: "Instance Name", value: "name" },
  groupName: { label: "Group Name", value: "name" },
  parent: {
    label: "GCP Project Name, AWS Account ID, or Azure Subscription ID",
    value: "id",
  },
  "full-resource-name": {
    label: "GCP Fully Qualified Resource Name",
    value: "full-resource-name",
  },
  accountId: { label: "Account ID", value: "id" },
  projectId: { label: "Project ID", value: "id" },
  region: { label: "AWS Region, GCP Zone, or Azure Region", value: "id" },
});

const rdpKeyOptions = recursiveReadonly({
  instanceName: { label: "Instance Name", value: "name" },
  "full-resource-name": {
    label: "Fully Qualified Resource Name",
    value: "full-resource-name",
  },
  region: { label: "Region", value: "id" },
});

export type ResourceFilterType<K extends IntegrationResourceRule["service"]> =
  keyof Extract<IntegrationResourceRule, { service: K }>["filters"];

export type ResourceFilterKeyOptions<
  K extends IntegrationResourceRule["service"],
> = {
  readonly [P in ResourceFilterType<K>]: FilterKeyOptionsByType;
};

type FilterKeyOptions = Array<{ label: string; value: string }>;
export type FilterKeyOptionsByType =
  | { readonly type: "boolean" }
  | { readonly type: "input" }
  | { readonly type: "options"; readonly options: FilterKeyOptions };

const getOptions = (
  options: FilterKeyOptions
): { readonly type: "options"; options: FilterKeyOptions } => ({
  type: "options",
  options,
});

const INPUT_FILTER_KEY = { type: "input" } as const;

const BOOLEAN_FILTER_KEY = { type: "boolean" } as const;

const awsFilterKeyOptions = recursiveReadonly({
  group: getOptions([keyOptions.name]),
  "permission-set": getOptions([keyOptions.arn, keyOptions.name]),
  policy: getOptions([keyOptions.arn]),
  resource: getOptions([
    keyOptions.name,
    keyOptions.arn,
    { label: "Service", value: "service" },
  ]),
  role: getOptions([keyOptions.name]),
  tag: INPUT_FILTER_KEY,
  account: getOptions([keyOptions.id]),
});

const azureAdFilterKeyOptions = recursiveReadonly({
  group: getOptions([keyOptions.id, keyOptions.label]),
  role: getOptions([keyOptions.id, keyOptions.label]),
});

const entraIdFilterKeyOptions = recursiveReadonly({
  group: getOptions([keyOptions.id, keyOptions.label]),
  role: getOptions([keyOptions.id, keyOptions.label]),
});

const azureFilterKeyOptions = recursiveReadonly({
  resource: getOptions([keyOptions.id, keyOptions.name]),
  role: getOptions([keyOptions.id, keyOptions.name]),
  subscription: getOptions([keyOptions.id]),
});

const gcloudFilterKeyOptions = recursiveReadonly({
  project: getOptions([keyOptions.id]),
  permission: getOptions([keyOptions.id]),
  role: getOptions([keyOptions.id]),
  resource: getOptions([
    keyOptions.name,
    keyOptions.type,
    keyOptions["full-resource-name"],
  ]),
});

const githubFilterKeyOptions = recursiveReadonly({
  team: getOptions([keyOptions.name]),
});

const customIntegrationFilterKeyOptions = recursiveReadonly({
  integration: getOptions([keyOptions.id]),
  parameter: getOptions([keyOptions.id]),
});

const k8sFilterKeyOptions = recursiveReadonly({
  role: getOptions([keyOptions.name]),
  resource: getOptions([
    { label: "Kind", value: "kind" },
    { label: "Namespace", value: "namespace" },
    keyOptions.name,
  ]),
  cluster: getOptions([keyOptions.name]),
});

const oktaFilterKeyOptions = recursiveReadonly({
  group: getOptions([keyOptions.id, keyOptions.label]),
});

const pgFilterKeyOptions = recursiveReadonly({
  role: getOptions([keyOptions.name]),
  instance: getOptions([keyOptions.name]),
  "row-level": getOptions([keyOptions.id]),
  "row-level-value": getOptions([{ label: "Value", value: "value" }]),
});

const mysqlFilterKeyOptions = recursiveReadonly({
  role: getOptions([keyOptions.name]),
  instance: getOptions([keyOptions.name]),
});

const selfHostedFilterKeyOptions = recursiveReadonly({
  environment: getOptions([keyOptions.id]),
  host: getOptions([
    keyOptions.id,
    { label: "Hostname", value: "hostname" },
    { label: "Public IP", value: "public-ip" },
    { label: "Fingerprint", value: "fingerprint" },
  ]),
  sudo: BOOLEAN_FILTER_KEY,
  user: getOptions([keyOptions.name]),
});

const snowflakeFilterKeyOptions = recursiveReadonly({
  role: getOptions([keyOptions.name]),
});

const sshFilterKeyOptions = recursiveReadonly({
  provider: getOptions([keyOptions.id]),
  destination: getOptions([
    // AWS
    sshKeyOptions.arn,
    // GCP
    sshKeyOptions["full-resource-name"],
    // AWS & GCP
    sshKeyOptions.instanceName,
  ]),
  // AWS & GCP
  parent: getOptions([sshKeyOptions.parent]),
  // AWS & GCP
  group: getOptions([sshKeyOptions.groupName]),
  // AWS & GCP
  region: getOptions([sshKeyOptions.region]),
  // AWS & GCP
  sudo: BOOLEAN_FILTER_KEY,
  // AWS & GCP
  all: BOOLEAN_FILTER_KEY,
});

// ENG-6341 ENG-6348 Azure RDP: Implement RDP filter key options
const rdpFilterKeyOptions = recursiveReadonly({
  destination: getOptions([
    rdpKeyOptions.instanceName,
    rdpKeyOptions["full-resource-name"],
  ]),
  provider: getOptions([keyOptions.id]),
  region: getOptions([rdpKeyOptions.region]),
  user: getOptions([keyOptions.name]),
});

const tailscaleFilterKeyOptions = recursiveReadonly({
  organization: getOptions([keyOptions.id]),
  device: getOptions([
    { label: "Node ID", value: "node-id" },
    { label: "Hostname", value: "hostname" },
    { label: "Node key", value: "node-key" },
    { label: "IPv4", value: "ipv4" },
    { label: "IPv6", value: "ipv6" },
    { label: "Full domain", value: "full-domain" },
  ]),
  tag: getOptions([keyOptions.name]),
});

const workspaceFilterKeyOptions = recursiveReadonly({
  group: getOptions([keyOptions.id, keyOptions.label]),
});

const oracleFilterKeyOptions = recursiveReadonly({
  domainId: getOptions([keyOptions.id, keyOptions.name]),
  compartmentId: getOptions([keyOptions.id, keyOptions.name]),
  groupId: getOptions([keyOptions.id, keyOptions.name]),
});

const splunkStackKeyOptions = recursiveReadonly({
  stackNode: { label: "Stack Node", value: "stackNode" },
});

const splunkStackFilterKeyOptions = recursiveReadonly({
  stackNode: getOptions([splunkStackKeyOptions.stackNode]),
  role: getOptions([keyOptions.id]),
});

const zpaFilterKeyOptions = recursiveReadonly({
  tenant: getOptions([keyOptions.id]),
  "application-segment": getOptions([keyOptions.name, keyOptions.id]),
});

const csaFilterKeyOptions = {
  organization: getOptions([keyOptions.id]),
  resource: getOptions([keyOptions.id, keyOptions.name]),
};

const windowsFilterKeyOptions = {
  account: getOptions([keyOptions.name]),
  domain: getOptions([keyOptions.id]),
};

const mcpServerFilterKeyOptions = recursiveReadonly({
  role: getOptions([keyOptions.name]),
});

export const filterKeyOptionsByResource: {
  readonly [K in IntegrationResourceRule["service"]]: ResourceFilterKeyOptions<K>;
} = {
  "azure-ad": azureAdFilterKeyOptions,
  "cisco-secure-access": csaFilterKeyOptions,
  "entra-id": entraIdFilterKeyOptions,
  "self-hosted": selfHostedFilterKeyOptions,
  "splunk-stack": splunkStackFilterKeyOptions,
  "zscaler-private-access": zpaFilterKeyOptions,
  aws: awsFilterKeyOptions,
  azure: azureFilterKeyOptions,
  custom: customIntegrationFilterKeyOptions,
  gcloud: gcloudFilterKeyOptions,
  github: githubFilterKeyOptions,
  k8s: k8sFilterKeyOptions,
  mcp: mcpServerFilterKeyOptions,
  mysql: mysqlFilterKeyOptions,
  oci: oracleFilterKeyOptions,
  okta: oktaFilterKeyOptions,
  pg: pgFilterKeyOptions,
  rdp: rdpFilterKeyOptions,
  snowflake: snowflakeFilterKeyOptions,
  ssh: sshFilterKeyOptions,
  tailscale: tailscaleFilterKeyOptions,
  windows: windowsFilterKeyOptions,
  workspace: workspaceFilterKeyOptions,
};
